import { useCallback, useMemo } from 'react';
import { BUDGET, fmt, netOf, num, pct1, todayLocalIso, type Payment } from '@piaip/shared';
import { sx, sxc } from '../sx';
import { useApp } from '../state';
import { api } from '../api';
import {
  BTN_3D,
  BTN_X,
  CARD_SHADOW,
  Empty,
  HINT,
  INPUT_CELL,
  INPUT_NUM,
  PageHead,
  SELECT_CELL,
  SERIF_H,
  StatTile,
  TD_CELL,
  TH_S,
  TH_S_R,
} from '../components/ui';
import { useRowEditor } from '../components/useRowEditor';

export function Payments() {
  const { engine, snapshot, can, mutate } = useApp();
  const canPay = can('payments');

  const list = useMemo(() => engine.payList(), [engine]);
  const totals = useMemo(() => {
    const all = snapshot.payments ?? [];
    const gross = all.reduce((a, p) => a + num(p.gross), 0);
    const ded = all.reduce((a, p) => a + num(p.retention) + num(p.tax), 0);
    const net = all.reduce((a, p) => a + netOf(p), 0);
    const byC: Record<string, number> = {};
    all.forEach((p) => {
      const k = (p.contractor || '').trim() || 'Contractor not stated';
      byC[k] = (byC[k] || 0) + netOf(p);
    });
    const keys = Object.keys(byC).sort((a, b) => byC[b] - byC[a]);
    const max = Math.max(1, ...keys.map((k) => byC[k]));
    return {
      count: all.length,
      gross,
      ded,
      net,
      bars: keys.map((k) => ({
        name: k,
        val: fmt(byC[k], 1),
        barW: `${Math.max(2, (byC[k] / max) * 100)}%`,
      })),
    };
  }, [snapshot]);

  const tiles = [
    {
      label: 'Gross value billed',
      value: fmt(totals.gross, 1),
      sub: `Rs million on ${totals.count} bills`,
      color: '#0d3b35',
    },
    {
      label: 'Deductions withheld',
      value: fmt(totals.ded, 1),
      sub: 'retention and taxes',
      color: '#b06a12',
    },
    {
      label: 'Net released',
      value: fmt(totals.net, 1),
      sub: 'Rs million paid to contractors',
      color: '#0f5c52',
    },
    {
      label: 'Of sanctioned cost',
      value: `${pct1((totals.net / BUDGET) * 100)}%`,
      sub: `of Rs ${fmt(BUDGET, 0)} Mn`,
      color: '#0d3b35',
    },
  ];

  return (
    <div
      style={sx(
        'max-width:1240px;margin:0 auto;padding:20px 16px 60px;display:flex;flex-direction:column;gap:18px',
      )}
    >
      <PageHead
        title="Payments to contractors"
        note={
          canPay
            ? 'Record every running bill or interim payment certificate released to a contractor. Net payments feed the financial statement immediately — they do not wait for weekly approval.'
            : 'Payments released to contractors, recorded by the PMU.'
        }
        action={
          canPay ? (
            <button
              type="button"
              className="pms-btn-lift"
              onClick={() =>
                void mutate(() =>
                  api.createPayment({ date: todayLocalIso(), cat: 'civil', gross: 0, retention: 0, tax: 0 }),
                )
              }
              style={sx(BTN_3D)}
            >
              + Record payment
            </button>
          ) : undefined
        }
      />

      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:12px')}>
        {tiles.map((t) => (
          <StatTile key={t.label} tile={t} />
        ))}
      </div>

      <div style={sx(CARD_SHADOW)}>
        <div style={sxc(SERIF_H, 'margin-bottom:2px')}>Register of running bills</div>
        <div style={sxc(HINT, 'margin-bottom:14px')}>
          Net paid is worked out as gross less retention and taxes. All figures in Rs million.
        </div>
        <div style={sx('overflow-x:auto')}>
          <table style={sx('width:100%;border-collapse:collapse;min-width:1040px')}>
            <thead>
              <tr>
                <th style={sxc(TH_S, 'width:140px')}>Date</th>
                <th style={sxc(TH_S, 'width:110px')}>IPC / bill</th>
                <th style={sxc(TH_S, 'width:170px')}>Package</th>
                <th style={sx(TH_S)}>Contractor</th>
                <th style={sxc(TH_S, 'width:120px')}>Head</th>
                <th style={sxc(TH_S_R, 'width:100px')}>Gross</th>
                <th style={sxc(TH_S_R, 'width:100px')}>Retention</th>
                <th style={sxc(TH_S_R, 'width:100px')}>Taxes</th>
                <th style={sxc(TH_S_R, 'width:100px')}>Net paid</th>
                <th style={sx('border-bottom:1px solid #dbe3e0')} />
              </tr>
            </thead>
            <tbody>
              {list
                .slice()
                .reverse()
                .map((p) => (
                  <PaymentRow key={p.id} payment={p} disabled={!canPay} />
                ))}
            </tbody>
            <tfoot>
              <tr style={sx('background:#f2f6f5')}>
                <td colSpan={5} style={sx('padding:9px 8px;font-size:12.5px;font-weight:600')}>
                  Total
                </td>
                <td
                  style={sx(
                    "padding:9px 8px;text-align:right;font-family:'IBM Plex Mono',monospace;font-size:13px;font-weight:600",
                  )}
                >
                  {fmt(totals.gross, 3)}
                </td>
                <td
                  colSpan={2}
                  style={sx(
                    "padding:9px 8px;text-align:right;font-family:'IBM Plex Mono',monospace;font-size:13px;color:#b06a12",
                  )}
                >
                  less {fmt(totals.ded, 3)}
                </td>
                <td
                  style={sx(
                    "padding:9px 8px;text-align:right;font-family:'IBM Plex Mono',monospace;font-size:13px;font-weight:700;color:#0f5c52",
                  )}
                >
                  {fmt(totals.net, 3)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
        {list.length === 0 ? (
          <div style={sx('margin-top:14px')}>
            <Empty pad="30px 16px">No payment has been recorded yet.</Empty>
          </div>
        ) : null}
      </div>

      {totals.bars.length ? (
        <div style={sx(CARD_SHADOW)}>
          <div style={sxc(SERIF_H, 'margin-bottom:14px')}>Net released, by contractor</div>
          <div style={sx('display:flex;flex-direction:column;gap:11px')}>
            {totals.bars.map((b) => (
              <div key={b.name} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap')}>
                <div style={sx('width:190px;font-size:13px;flex:none')}>{b.name}</div>
                <div
                  style={sx(
                    'flex:1;min-width:120px;height:18px;background:#eef2f1;border-radius:4px;overflow:hidden;box-shadow:0 1px 3px rgba(13,59,53,.15) inset',
                  )}
                >
                  <div
                    style={sxc(
                      'height:100%;background-color:#14766a;background-image:linear-gradient(180deg,rgba(255,255,255,.4),rgba(255,255,255,0) 55%,rgba(0,0,0,.18))',
                      `width:${b.barW}`,
                    )}
                  />
                </div>
                <div
                  style={sx(
                    "width:90px;text-align:right;font-family:'IBM Plex Mono',monospace;font-size:13px;flex:none",
                  )}
                >
                  {b.val}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * One running bill. The row keeps its own copy while it is being typed into and
 * writes the change back when the field is left, so the net-paid figure updates
 * as you go without a request per keystroke.
 */
function PaymentRow({ payment, disabled }: { payment: Payment; disabled: boolean }) {
  const { snapshot, mutate } = useApp();
  const save = useCallback(
    async (patch: Partial<Payment>) => {
      await mutate(() => api.patchPayment(payment.id, patch));
    },
    [payment.id, mutate],
  );
  const { row, set, commit, commitNow } = useRowEditor(payment, save);

  return (
    <tr>
      <td style={sx(TD_CELL)}>
        <input
          type="date"
          aria-label="Bill date"
          value={row.date}
          disabled={disabled}
          onChange={(e) => set({ date: e.target.value })}
          onBlur={() => void commit()}
          style={sx(
            'padding:8px;border:1px solid #d3dcda;border-radius:4px;font-size:13.5px;width:100%;box-sizing:border-box',
          )}
        />
      </td>
      <td style={sx(TD_CELL)}>
        <input
          type="text"
          aria-label="IPC or bill number"
          value={row.ipc}
          disabled={disabled}
          onChange={(e) => set({ ipc: e.target.value })}
          onBlur={() => void commit()}
          style={sx(
            "padding:8px;border:1px solid #d3dcda;border-radius:4px;font-size:13.5px;width:100%;box-sizing:border-box;font-family:'IBM Plex Mono',monospace",
          )}
        />
      </td>
      <td style={sx(TD_CELL)}>
        <select
          aria-label="Contract package"
          value={row.pkgId}
          disabled={disabled}
          onChange={(e) => {
            const pkg = (snapshot.packages ?? []).find((x) => x.id === e.target.value);
            const patch: Partial<Payment> = { pkgId: e.target.value };
            // Naming the package fills the contractor in, unless one is already stated.
            if (pkg && !row.contractor) patch.contractor = pkg.contractor || pkg.name || '';
            void commitNow(patch);
          }}
          style={sx(SELECT_CELL)}
        >
          <option value="">— not linked —</option>
          {(snapshot.packages ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name || 'Unnamed package'}
            </option>
          ))}
        </select>
      </td>
      <td style={sx(TD_CELL)}>
        <input
          type="text"
          aria-label="Contractor"
          value={row.contractor}
          disabled={disabled}
          onChange={(e) => set({ contractor: e.target.value })}
          onBlur={() => void commit()}
          style={sxc(INPUT_CELL, 'min-width:140px')}
        />
      </td>
      <td style={sx(TD_CELL)}>
        <select
          aria-label="Head of account"
          value={row.cat}
          disabled={disabled}
          onChange={(e) => void commitNow({ cat: e.target.value as Payment['cat'] })}
          style={sx(SELECT_CELL)}
        >
          <option value="civil">Civil</option>
          <option value="mech">Mechanical</option>
          <option value="elec">Electrical</option>
          <option value="misc">Miscellaneous</option>
        </select>
      </td>
      {(['gross', 'retention', 'tax'] as const).map((field) => (
        <td key={field} style={sx(TD_CELL)}>
          <input
            type="number"
            aria-label={field === 'tax' ? 'Taxes' : field === 'gross' ? 'Gross amount' : 'Retention'}
            value={String(row[field] ?? '')}
            disabled={disabled}
            onChange={(e) => set({ [field]: e.target.value } as Partial<Payment>)}
            onBlur={() => void commit()}
            style={sx(field === 'gross' ? INPUT_NUM : `${INPUT_CELL};text-align:right`)}
          />
        </td>
      ))}
      <td
        style={sx(
          "padding:5px 8px;border-bottom:1px solid #eef2f1;text-align:right;font-family:'IBM Plex Mono',monospace;font-size:13.5px;font-weight:600;color:#0f5c52",
        )}
      >
        {fmt(netOf(row), 3)}
      </td>
      <td style={sx(TD_CELL)}>
        <button
          type="button"
          aria-label="Remove this bill"
          disabled={disabled}
          onClick={() => {
            if (window.confirm('Remove this bill from the register?')) {
              void mutate(() => api.deletePayment(payment.id));
            }
          }}
          style={sx(BTN_X)}
        >
          ×
        </button>
      </td>
    </tr>
  );
}
